import { useParams, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { getPostBySlug } from '../data/blogPosts';
import { useTranslation } from 'react-i18next';

export default function BlogPost() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  if (!post) {
    return (
      <div className="pt-32 pb-24 max-w-3xl mx-auto px-4">
        <h1 className="text-3xl font-bold mb-4">Post Not Found</h1>
        <p className="text-slate-600 mb-6">The article you are looking for does not exist.</p>
        <Link to="/blog" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-700 transition">Back to Blog</Link>
      </div>
    );
  }

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    'headline': post.title,
    'datePublished': post.published,
    ...(post.image ? { 'image': post.image } : {}),
    ...(post.seo?.keywords ? { 'keywords': post.seo.keywords } : (post.tags?.length ? { 'keywords': post.tags.join(', ') } : {})),
    'author': {
      '@type': 'Organization',
      'name': 'Vigor Auto Repair Garage'
    },
    'description': post.seo?.description || post.excerpt
  };

  return (
    <>
      <SEO
        title={post.title}
        description={post.seo?.description || post.excerpt}
        keywords={post.seo?.keywords || (post.tags?.join(', '))}
        image={post.image || '/logo512.png'}
        schema={schema}
        type="article"
      />
      <article className="pt-20 md:pt-28 pb-12 md:pb-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {post.image && (
            <div className="-mt-2 -mx-2 mb-6 overflow-hidden rounded-xl">
              <img src={post.image} alt={post.title} className="w-full h-56 object-cover" />
            </div>
          )}
          <p className="text-xs text-slate-500 mb-3">{new Date(post.published).toLocaleDateString()} • {post.readingMinutes} min read</p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-6 text-slate-900">{post.title}</h1>
          <div className="prose prose-slate max-w-none text-slate-700 text-sm md:text-base leading-relaxed whitespace-pre-line">
            {post.content}
          </div>

          {/* Mid-article Vigor Auto promo */}
          <div className="mt-10 rounded-2xl bg-gradient-to-r from-blue-900 via-blue-800 to-indigo-900 text-white shadow-xl border border-blue-700/60 p-6 md:p-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-blue-200/80">Need Expert Hands?</p>
                <h2 className="mt-2 text-xl md:text-2xl font-bold">Book Your Vigor Auto Repair Service Today</h2>
                <p className="mt-3 text-sm md:text-base text-blue-100/90 max-w-2xl">
                  From diagnostics and dealer-alternative servicing to AC performance restoration, our master technicians keep your vehicle ready for UAE roads. Schedule a visit while the insights from this article are top of mind.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
                <Link
                  to="/request-quote"
                  className="inline-flex items-center justify-center rounded-lg bg-[#F5D313] text-slate-900 font-semibold px-6 py-3 text-sm shadow hover:bg-[#e4c40f] transition w-full sm:w-40"
                >
                  {t('hero.cta.quote')}
                </Link>
                <a
                  href="https://wa.me/971524333690?text=Hi%20Vigor%20Auto%2C%20I%27d%20like%20to%20book%20a%20service."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded-lg border border-white/30 text-white font-semibold px-6 py-3 text-sm hover:bg-white/10 transition w-full sm:w-40"
                >
                  {t('nav.whatsapp')}
                </a>
              </div>
            </div>
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            {post.tags?.map(tag => (
              <span key={tag} className="inline-flex items-center px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-[11px] font-medium">#{tag}</span>
            ))}
          </div>
          <div className="mt-12 flex gap-4">
            <Link to="/blog" className="inline-flex rounded-lg bg-slate-900 px-5 py-2.5 text-xs font-semibold text-white hover:bg-slate-700 transition">{t('blog.title')}</Link>
            <Link to="/services" className="inline-flex rounded-lg border border-slate-300 px-5 py-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 transition">{t('services.title')}</Link>
          </div>
          <div className="mt-12 rounded-3xl bg-gradient-to-br from-emerald-500/15 via-emerald-500/10 to-blue-500/15 border border-emerald-500/30 p-6 md:p-8 shadow-lg">
            <h2 className="text-xl md:text-2xl font-bold text-slate-900">Ready for hassle-free car care?</h2>
            <p className="mt-3 text-sm md:text-base text-slate-700 max-w-2xl">
              Vigor Auto Repair is your dealer-alternative workshop in Dubai. Call, message, or drop by Ras Al Khor—we’ll tailor diagnostics, preventive service, or repairs to your driving needs.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="tel:+971568699800"
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-6 py-3 text-sm font-semibold text-white shadow hover:bg-emerald-500 transition w-full sm:w-auto min-w-[160px]"
              >
                Call +971 56 869 9800
              </a>
              <Link
                to="/request-quote"
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-emerald-500 px-6 py-3 text-sm font-semibold text-emerald-700 hover:bg-emerald-500/10 transition w-full sm:w-auto min-w-[160px]"
              >
                Request A Quote
              </Link>
              <a
                href="https://maps.app.goo.gl/TUp25rLZUxxKZjAn8"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-emerald-500 px-6 py-3 text-sm font-semibold text-emerald-700 hover:bg-emerald-500/10 transition w-full sm:w-auto min-w-[160px]"
              >
                {t('nav.rasAlKhor')}
              </a>
            </div>
          </div>
        </div>
      </article>
    </>
  );
}
